import type { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { BOXING, MMA } from 'constants/routes';
import { parseToPlainText } from 'helpers/textEditor';
import { getEnvLink } from 'helpers/routes';
import { getCheapestActiveAma } from 'helpers/ama';

export type AthleteProfileSeoData = {
  h1: string;
  metaTitle: string;
  metaDescription: string;
  structuredData: Record<string, unknown>[];
};

const TITLE_LIMIT = 60;
const DESC_LIMIT = 170;
const SPORT_BREADCRUMB_ROUTES: Record<string, string> = {
  boxing: BOXING,
  mma: MMA,
};

const CONTENT_FLAGS: { flag: keyof GetStoreBySlug_store; label: string }[] = [
  { flag: 'hasAma', label: 'AMAs' },
  { flag: 'hasMerch', label: 'Merch' },
  { flag: 'hasMemorabilia', label: 'Autographed Memorabilia' },
  { flag: 'hasStreams', label: 'Streams' },
  { flag: 'hasMediaPost', label: 'Videos' },
  { flag: 'hasProducts', label: 'Products' },
  { flag: 'hasExperiences', label: 'Experiences' },
];

function getContentLabels(store: GetStoreBySlug_store): string[] {
  return CONTENT_FLAGS.filter(({ flag }) => store[flag]).map(
    ({ label }) => label
  );
}

function buildContentSuffix(labels: string[]): string {
  if (labels.length === 0) return '';
  const top2 = labels.slice(0, 2);
  return labels.length >= 3 ? `${top2.join(', ')} & More` : top2.join(', ');
}

function buildTitle(
  storeName: string,
  storeTitle: string | null | undefined,
  sport: string | undefined,
  contentLabels: string[]
): string {
  const join = (parts: string[]) => parts.join(' | ').replace(' | — ', ' — ');

  const middle: string[] = [];
  if (storeTitle) middle.push(`— ${storeTitle}`);
  if (sport) middle.push(sport);

  const fullSuffix = buildContentSuffix(contentLabels);
  const firstLabel = contentLabels[0] ?? '';
  const suffixes = [fullSuffix, firstLabel, ''].filter(
    (s, i, arr) => arr.indexOf(s) === i
  );

  // Prefer keeping more middle segments; for each keep level try suffixes in order
  for (let keep = middle.length; keep >= 0; keep--) {
    const mid = middle.slice(0, keep);
    for (const suffix of suffixes) {
      const parts = [
        storeName,
        ...mid,
        ...(suffix ? [suffix] : []),
        'MILLIONS',
      ];
      const candidate = join(parts);
      if (candidate.length <= TITLE_LIMIT) return candidate;
    }
  }

  return join([storeName, 'MILLIONS']);
}

const DESCRIPTION_CONTENT_FLAGS: {
  flag: keyof GetStoreBySlug_store;
  label: string;
}[] = [
  { flag: 'hasAma', label: 'personalized video Q&As' },
  { flag: 'hasMerch', label: 'official merch' },
  { flag: 'hasMemorabilia', label: 'signed memorabilia' },
  { flag: 'hasStreams', label: 'exclusive live streams' },
  { flag: 'hasMediaPost', label: 'videos' },
  { flag: 'hasProducts', label: 'products' },
  { flag: 'hasExperiences', label: 'fan experiences' },
];

/** Streams and videos are watched, not bought — they lead with "Watch". */
const WATCH_LABELS: { flag: keyof GetStoreBySlug_store; label: string }[] = [
  { flag: 'hasStreams', label: 'live streams' },
  { flag: 'hasMediaPost', label: 'videos' },
];

const isWatchable = (flag: keyof GetStoreBySlug_store): boolean =>
  WATCH_LABELS.some((entry) => entry.flag === flag);

function joinLabels(labels: string[]): string {
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
}

/**
 * Greedily keeps as many labels as fit, dropping from the end. Returns '' when
 * even one label overflows, so callers decide whether to force the first.
 */
function pickLabelsWithinBudget(labels: string[], budget: number): string {
  for (let count = labels.length; count >= 1; count--) {
    const candidate = joinLabels(labels.slice(0, count));

    if (candidate.length <= budget) {
      return candidate;
    }
  }

  return '';
}

function buildDescription(
  store: GetStoreBySlug_store,
  storeName: string
): string {
  const storeTitle = store.storeDetails?.storeTitle;
  const sport = store.sports?.[0]?.name;

  // Closing identity: role or sport anchors the profile context after the offer
  const closing = storeTitle
    ? ` — ${storeTitle} on MILLIONS.`
    : sport
    ? ` — ${sport} on MILLIONS.`
    : ' on MILLIONS.';

  const shopLabels = DESCRIPTION_CONTENT_FLAGS.filter(
    ({ flag }) => flag !== 'hasAma' && store[flag]
  ).map(({ label }) => label);

  // No content — follow CTA with as many hashtags as fit within budget
  if (!store.hasAma && shopLabels.length === 0) {
    const base = `Follow ${storeName} on MILLIONS`;
    const contextParts = [storeTitle, sport].filter(Boolean) as string[];
    const hashtagNames = store.hashtags.map((h) => h.name);

    for (let count = hashtagNames.length; count >= 0; count--) {
      const hashtagStr =
        count > 0 ? hashtagNames.slice(0, count).join(' · ') : '';
      const allParts = [...contextParts, hashtagStr].filter(Boolean);
      const context = allParts.join(' | ');
      const candidate = context ? `${base} — ${context}.` : `${base}.`;
      if (candidate.length <= DESC_LIMIT) return candidate;
    }

    return `${base}.`;
  }

  // Has AMA — lead with "Ask {Name} a question"
  if (store.hasAma) {
    const lead = `Ask ${storeName} a question`;
    if (shopLabels.length === 0) {
      return `${lead}${closing}`.slice(0, DESC_LIMIT);
    }
    const shopPrefix = ', shop ';
    const shopBudget =
      DESC_LIMIT - lead.length - shopPrefix.length - closing.length;
    let shopList = '';
    for (let count = shopLabels.length; count >= 1; count--) {
      const candidate = joinLabels(shopLabels.slice(0, count));
      if (candidate.length <= shopBudget) {
        shopList = candidate;
        break;
      }
    }
    return (shopList
      ? `${lead}${shopPrefix}${shopList}${closing}`
      : `${lead}${closing}`
    ).slice(0, DESC_LIMIT);
  }

  // Lead with the verb matching what they have: "Watch" if anything is
  // watchable, otherwise "Shop".
  const watchLabels = WATCH_LABELS.filter(({ flag }) => store[flag]).map(
    ({ label }) => label
  );
  const buyLabels = DESCRIPTION_CONTENT_FLAGS.filter(
    ({ flag }) => flag !== 'hasAma' && !isWatchable(flag) && store[flag]
  ).map(({ label }) => label);

  const leadLabels = watchLabels.length ? watchLabels : buyLabels;
  const trailLabels = watchLabels.length ? buyLabels : [];
  const lead = watchLabels.length
    ? `Watch ${storeName}'s exclusive`
    : `Shop ${storeName}'s`;

  const trailPrefix = trailLabels.length ? ', shop ' : '';
  const budget =
    DESC_LIMIT - lead.length - 1 - trailPrefix.length - closing.length;

  const leadList = pickLabelsWithinBudget(leadLabels, budget) || leadLabels[0];
  const trailList = pickLabelsWithinBudget(
    trailLabels,
    budget - leadList.length
  );

  return `${lead} ${leadList}${
    trailList ? `${trailPrefix}${trailList}` : ''
  }${closing}`.slice(0, DESC_LIMIT);
}

function buildPersonSchema(
  store: GetStoreBySlug_store,
  storeName: string,
  slug: string,
  plainBio: string
): Record<string, unknown> {
  const baseUrl = getEnvLink();
  const profileUrl = `${baseUrl}/${slug}`;
  const storeTitle = store.storeDetails?.storeTitle;
  const avatarURL = store.storeDetails?.avatarURL;
  const sport = store.sports?.[0]?.name;

  const sameAs = store.socialMedia
    .map((s) => s.url)
    .filter(
      (url): url is string =>
        typeof url === 'string' && url.startsWith('https://')
    );

  const person: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['Person', 'Athlete'],
    name: storeName,
    url: profileUrl,
  };

  if (storeTitle) person.jobTitle = storeTitle;
  if (avatarURL) person.image = avatarURL;
  if (plainBio) person.description = plainBio;
  if (sport) person.sport = sport;
  if (sameAs.length) person.sameAs = sameAs;

  const knowsAboutTopics = [
    sport,
    ...(store.hashtags?.map((h) => h.name) ?? []),
  ].filter((t): t is string => Boolean(t));
  if (knowsAboutTopics.length) person.knowsAbout = knowsAboutTopics;

  if (store.hasAma && store.amas) {
    const cheapest = getCheapestActiveAma(store.amas);
    if (cheapest) {
      // Person uses makesOffer (not offers) per schema.org
      person.makesOffer = {
        '@type': 'Offer',
        name: `Ask ${storeName} a Question`,
        price: cheapest.price,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: profileUrl,
      };
    }
  }

  return person;
}

function buildNextStreamSchema(
  store: GetStoreBySlug_store,
  storeName: string,
  slug: string
): Record<string, unknown> | null {
  const stream = store.nextStream;
  if (!stream?.name) return null;

  const baseUrl = getEnvLink();
  const profileUrl = `${baseUrl}/${slug}`;

  const event: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: stream.name,
    url: `${baseUrl}/stream/${stream.slug}`,
    organizer: {
      '@type': 'Person',
      name: storeName,
      url: profileUrl,
    },
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
  };

  if (stream.scheduleDate) event.startDate = stream.scheduleDate;
  if (stream.imageURL) {
    event.image = {
      '@type': 'ImageObject',
      url: stream.imageURL,
      name: stream.name,
    };
  }
  if (stream.description) event.description = stream.description;
  if (!stream.isFree && stream.price > 0) {
    event.offers = {
      '@type': 'Offer',
      price: stream.price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: `${baseUrl}/stream/${stream.slug}`,
    };
  }

  return event;
}

function buildBreadcrumbSchema(
  storeName: string,
  slug: string,
  sport: string | undefined
): Record<string, unknown> {
  const baseUrl = getEnvLink();
  const normalizedSport = sport?.trim().toLowerCase();
  const sportRoute = normalizedSport
    ? SPORT_BREADCRUMB_ROUTES[normalizedSport]
    : undefined;

  const items: Record<string, unknown>[] = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
  ];

  if (sport && sportRoute) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: sport,
      item: `${baseUrl}${sportRoute}`,
    });
  }

  items.push({
    '@type': 'ListItem',
    position: sportRoute ? 3 : 2,
    name: storeName,
    item: `${baseUrl}/${slug}`,
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function buildProfilePageSchema(
  storeName: string,
  slug: string,
  plainBio: string,
  avatarURL?: string | null
): Record<string, unknown> {
  const baseUrl = getEnvLink();
  const profileUrl = `${baseUrl}/${slug}`;
  // Google reads the result thumbnail from mainEntity.image; primaryImageOfPage alone is not enough.
  const mainEntity: Record<string, unknown> = {
    '@type': 'Person',
    name: storeName,
    alternateName: `@${slug}`,
    url: profileUrl,
  };

  if (avatarURL) {
    mainEntity.image = avatarURL;
  }

  const profilePage: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: storeName,
    url: profileUrl,
    mainEntity,
  };

  if (plainBio) {
    profilePage.description = plainBio;
  }

  if (avatarURL) {
    profilePage.primaryImageOfPage = avatarURL;
  }

  return profilePage;
}

function buildH1(
  storeName: string,
  storeTitle: string | null | undefined,
  sport: string | undefined,
  contentSuffix: string
): string {
  const segments: string[] = [storeName];

  if (storeTitle) {
    segments.push(`— ${storeTitle}`);
  }

  if (sport) {
    segments.push(sport);
  }

  let h1 = segments.join(' | ').replace(' | — ', ' — ');

  if (h1.length < 20 && contentSuffix) {
    h1 = `${h1} | ${contentSuffix}`;
  }

  if (h1.length < 20) {
    h1 = `${h1} on MILLIONS`;
  }

  if (h1.length > 70) {
    h1 = h1
      .slice(0, 70)
      .replace(/\s+\S*$/, '')
      .replace(/[,|–—\s]+$/, '');
  }

  return h1;
}

export function getAthleteProfileSeo(
  store: GetStoreBySlug_store,
  slug: string
): AthleteProfileSeoData {
  const storeName =
    store.storeDetails?.storeName || `${store.firstName} ${store.lastName}`;

  const sport = store.sports?.[0]?.name;
  const storeTitle = store.storeDetails?.storeTitle;
  const contentLabels = getContentLabels(store);

  const rawBio =
    store.storeDetails?.description || store.storeDetails?.bio || '';
  const plainBio = parseToPlainText(rawBio).trim();

  const nextStreamSchema = buildNextStreamSchema(store, storeName, slug);

  return {
    h1: buildH1(
      storeName,
      storeTitle,
      sport,
      buildContentSuffix(contentLabels)
    ),
    metaTitle: buildTitle(storeName, storeTitle, sport, contentLabels),
    metaDescription: buildDescription(store, storeName),
    structuredData: [
      buildPersonSchema(store, storeName, slug, plainBio),
      buildBreadcrumbSchema(storeName, slug, sport),
      buildProfilePageSchema(
        storeName,
        slug,
        plainBio,
        store.storeDetails?.avatarURL
      ),
      ...(nextStreamSchema ? [nextStreamSchema] : []),
    ],
  };
}
